/**
 * Excel Data Extractor for Smart Match
 * Intelligently detects and extracts data from any sheet with tabular data
 * 
 * Auto-detects:
 * - Header row location (searches first 10 rows)
 * - Column headers (non-empty, non-numeric values)
 * - Data rows (starts after header row)
 * 
 * Known sheets (with predefined header rows for optimization):
 * - Advertising Raw Data: Row 4 = Column Headers, Row 5+ = Data
 * - Sales Raw Data: Row 5 = Column Headers, Row 6+ = Data
 * - Any other sheet: Auto-detected
 */

import { EXCEL_DATE } from "./constants";

export interface ExcelTableData {
  sheetName: string;
  headers: string[];      // Detected column headers
  data: any[][];          // Data rows after headers
  rowCount: number;
  columnCount: number;
  range: string;
  headerRowIndex: number; // The row index where headers were found (0-based)
}

/**
 * Known sheet names with predefined header row positions (for optimization)
 */
export const KNOWN_SHEETS = {
  SALES: "Sales Raw Data",
  ADVERTISING: "Advertising Raw Data"
} as const;

/**
 * Get available data source sheets in the workbook
 * Returns visible sheets that contain at least one Excel Table (sheet.tables.count > 0).
 * Sheets without tables (dashboards, JESS reports, chart sheets) are excluded.
 */
export async function getAvailableDataSources(): Promise<string[]> {
  try {
    return await Excel.run(async (context) => {
      const worksheets = context.workbook.worksheets;
      worksheets.load("items/name, items/visibility");
      await context.sync();

      // Filter to visible sheets and batch-load table counts
      const visibleSheets = worksheets.items.filter(
        (sheet) => sheet.visibility === Excel.SheetVisibility.visible
      );
      for (const sheet of visibleSheets) {
        sheet.tables.load("count");
      }
      await context.sync();

      // Only include sheets that have at least one Excel Table
      return visibleSheets
        .filter((sheet) => sheet.tables.count > 0)
        .map((sheet) => sheet.name);
    });
  } catch (error) {
    console.error("Error getting available data sources:", error);
    throw new Error("Failed to load available data sources");
  }
}

/**
 * Intelligently detect the header row in a range of values
 * Headers are typically:
 * - Text values (not purely numeric)
 * - In a row where most cells are non-empty
 * - Before rows with similar structure (data rows)
 * 
 * @param values - 2D array of cell values
 * @param maxSearchRows - Maximum rows to search for headers (default 10)
 * @returns Header row index (0-based) or -1 if not found
 */
function detectHeaderRow(values: any[][], maxSearchRows: number = 10): number {
  const searchLimit = Math.min(maxSearchRows, values.length);
  
  for (let rowIndex = 0; rowIndex < searchLimit; rowIndex++) {
    const row = values[rowIndex];
    
    // Skip completely empty rows
    const nonEmptyCells = row.filter(cell => 
      cell !== null && cell !== undefined && cell !== ""
    );
    
    if (nonEmptyCells.length === 0) {
      continue;
    }
    
    // Check if this row looks like a header row
    const isLikelyHeader = checkIfHeaderRow(row, values, rowIndex);
    
    if (isLikelyHeader) {
      console.log(`   🎯 Detected header row at index ${rowIndex} (Row ${rowIndex + 1})`);
      return rowIndex;
    }
  }
  
  console.warn(`   ⚠️ Could not detect header row in first ${searchLimit} rows`);
  return -1;
}

/**
 * Check if a row looks like a header row
 * Headers typically have:
 * - High percentage of non-empty cells
 * - Mostly text values (not purely numeric)
 * - Different from the row above (if exists)
 * - Similar structure to rows below (data rows)
 */
function checkIfHeaderRow(row: any[], allRows: any[][], rowIndex: number): boolean {
  // Count non-empty cells
  const nonEmptyCells = row.filter(cell => 
    cell !== null && cell !== undefined && cell !== ""
  );
  
  // Must have at least 2 non-empty cells
  if (nonEmptyCells.length < 2) {
    return false;
  }
  
  // Calculate percentage of non-empty cells
  const fillRatio = nonEmptyCells.length / row.length;
  
  // Header should have decent fill ratio (at least 30%)
  if (fillRatio < 0.3) {
    return false;
  }
  
  // Count text vs numeric cells
  let textCount = 0;
  let numericCount = 0;
  
  for (const cell of nonEmptyCells) {
    if (typeof cell === "string") {
      textCount++;
    } else if (typeof cell === "number") {
      numericCount++;
    }
  }
  
  // Headers are typically mostly text
  // But allow some numbers (like "Q1", "2024", etc.)
  const textRatio = textCount / nonEmptyCells.length;
  
  // Must have at least 50% text cells
  if (textRatio < 0.5) {
    return false;
  }
  
  // Check if there are data rows below (at least 1)
  if (rowIndex + 1 >= allRows.length) {
    return false;
  }
  
  // Check the next row to see if it looks like data
  const nextRow = allRows[rowIndex + 1];
  const nextRowNonEmpty = nextRow.filter(cell => 
    cell !== null && cell !== undefined && cell !== ""
  );
  
  // Next row should also have data
  if (nextRowNonEmpty.length < 2) {
    return false;
  }
  
  return true;
}

/**
 * Extract table data from a specific sheet
 * Intelligently detects header row for any sheet
 * Uses predefined positions for known sheets (Sales/Advertising) for optimization
 * 
 * @param sheetName - Name of the sheet to extract data from
 * @returns ExcelTableData with headers, data, and metadata
 */
export async function extractTableData(sheetName: string): Promise<ExcelTableData> {
  try {
    return await Excel.run(async (context) => {
      const worksheets = context.workbook.worksheets;
      worksheets.load("items/name");
      await context.sync();

      // Find the specified sheet
      const sheet = worksheets.items.find(s => s.name === sheetName);
      if (!sheet) {
        throw new Error(`Sheet "${sheetName}" not found`);
      }

      // Get the used range
      const usedRange = sheet.getUsedRange();
      console.log("useedRage", usedRange)

      
      if (!usedRange) {
        throw new Error(`No data found in sheet "${sheetName}"`);
      }

      const visibleView = usedRange.getVisibleView();
      console.log("visible veiw", visibleView)

      visibleView.load(["values", "rowCount", "columnCount", "address"]);
      await context.sync();

      console.log(`📋 Extracting data from "${sheetName}"`);
      // console.log(`   Range: ${visibleView.address}`);
      console.log(`   Dimensions: ${visibleView.rowCount} rows × ${visibleView.columnCount} columns`);

      // Determine header row index
      let HEADER_ROW_INDEX: number;
      
      // Use predefined positions for known sheets (optimization)
      if (sheetName === KNOWN_SHEETS.SALES) {
        HEADER_ROW_INDEX = 4; // Row 5
        console.log(`   📌 Using predefined header row for Sales Raw Data`);
      } else if (sheetName === KNOWN_SHEETS.ADVERTISING) {
        HEADER_ROW_INDEX = 3; // Row 4
        console.log(`   📌 Using predefined header row for Advertising Raw Data`);
      } else {
        // Intelligent detection for unknown sheets
        console.log(`   🔍 Auto-detecting header row...`);
        HEADER_ROW_INDEX = detectHeaderRow(visibleView.values);
        
        if (HEADER_ROW_INDEX === -1) {
          throw new Error(
            `Could not detect header row in sheet "${sheetName}". ` +
            `Please ensure your data has a clear header row with column names.`
          );
        }
      }

      const DATA_START_INDEX = HEADER_ROW_INDEX + 1;

      // Validate sheet structure
      if (visibleView.rowCount <= DATA_START_INDEX) {
        throw new Error(
          `Sheet "${sheetName}" doesn't have data rows after the header. ` +
          `Found headers at Row ${HEADER_ROW_INDEX + 1}, but no data rows below.`
        );
      }

      // Extract headers from the detected row
      const headerRow = visibleView.values[HEADER_ROW_INDEX];
      
      // Clean headers (remove null/undefined/empty values)
      const headers: string[] = headerRow
        .map((h: any) => (h !== null && h !== undefined && h !== "" ? String(h).trim() : ""))
        .filter((h: string) => h !== "");

      if (headers.length === 0) {
        throw new Error(`No column headers found in Row ${HEADER_ROW_INDEX + 1} of sheet "${sheetName}"`);
      }


      // Extract data rows (starts from one row after headers)
      const dataRows = visibleView.values.slice(DATA_START_INDEX);
      
      // Filter out completely empty rows
      const cleanedData = dataRows.filter((row: any[]) => 
        row.some(cell => cell !== null && cell !== undefined && cell !== "")
      );

      // Detect date columns by analyzing sample data
      const dateColumnIndices = detectDateColumns(headers, cleanedData);
      if (dateColumnIndices.length > 0) {
        
        // Log sample values for debugging
        dateColumnIndices.forEach(colIndex => {
          const sampleValues = cleanedData.slice(0, 3).map(row => row[colIndex]);
          console.log(`   📅 Sample values for "${headers[colIndex]}": ${sampleValues.join(', ')}`);
        });
      } else {
        console.log(`   📅 No date columns detected`);
      }

      // Ensure data rows match header column count and format dates
      const normalizedData = cleanedData.map((row: any[], rowIndex: number) => {
        const normalizedRow = [];
        for (let i = 0; i < headers.length; i++) {
          const cellValue = row[i];
          let processedValue = cellValue !== null && cellValue !== undefined && cellValue !== "" 
            ? cellValue 
            : null;
          
          // Convert Excel date serial numbers to readable date strings
          if (processedValue !== null && dateColumnIndices.includes(i)) {
            const originalValue = processedValue;
            processedValue = formatExcelDate(processedValue);
            
            // Debug first few conversions
            if (rowIndex < 3) {
              console.log(`   📅 Row ${rowIndex + 1}, Column "${headers[i]}": ${originalValue} → ${processedValue}`);
            }
          }
          
          normalizedRow.push(processedValue);
        }
        return normalizedRow;
      });

      const tableData: ExcelTableData = {
        sheetName,
        headers,
        data: normalizedData,
        rowCount: normalizedData.length,
        columnCount: headers.length,
        range: `A${HEADER_ROW_INDEX + 1}:${getExcelColumnLetter(headers.length)}${HEADER_ROW_INDEX + 1 + normalizedData.length}`,
        headerRowIndex: HEADER_ROW_INDEX
      };

      return tableData;
    });
  } catch (error) {
    console.error(`Error extracting data from "${sheetName}":`, error);
    throw error;
  }
}

/**
 * Maximum rows to send to fuzzy match API
 * Limited to prevent 413 Request Entity Too Large errors
 */
export const MAX_FUZZY_MATCH_ROWS = 2000;

/**
 * Validate that the extracted table data is suitable for fuzzy matching
 * Now returns a warning if data will be truncated
 */
export function validateTableData(tableData: ExcelTableData): { 
  valid: boolean; 
  message?: string;
  warning?: string;
  willTruncate?: boolean;
  truncatedRows?: number;
} {
  if (!tableData) {
    return { valid: false, message: "Table data is null or undefined" };
  }

  if (tableData.headers.length === 0) {
    return { valid: false, message: "No column headers found" };
  }

  if (tableData.data.length === 0) {
    return { valid: false, message: "No data rows found" };
  }

  // If data exceeds limit, it will be truncated but still valid
  if (tableData.data.length > MAX_FUZZY_MATCH_ROWS) {
    return { 
      valid: true,
      warning: `Dataset has ${tableData.data.length.toLocaleString()} rows. Only the first ${MAX_FUZZY_MATCH_ROWS.toLocaleString()} rows will be used for matching to prevent server errors.`,
      willTruncate: true,
      truncatedRows: MAX_FUZZY_MATCH_ROWS
    };
  }

  return { valid: true };
}

/**
 * Truncate table data to maximum rows for fuzzy matching
 * This prevents 413 Request Entity Too Large errors
 */
export function truncateTableDataForFuzzyMatch(tableData: ExcelTableData): ExcelTableData {
  if (tableData.data.length <= MAX_FUZZY_MATCH_ROWS) {
    return tableData; 
  }

  console.log(`⚠️ Truncating table data from ${tableData.data.length} to ${MAX_FUZZY_MATCH_ROWS} rows`);
  
  return {
    ...tableData,
    data: tableData.data.slice(0, MAX_FUZZY_MATCH_ROWS),
    rowCount: MAX_FUZZY_MATCH_ROWS
  };
}

/**
 * Detect which columns contain date values by analyzing column headers and sample data
 * Excel dates are stored as serial numbers (days since 1900-01-01)
 * 
 * @param headers - Column headers
 * @param dataRows - Sample data rows to analyze
 * @returns Array of column indices that contain dates
 */
function detectDateColumns(headers: string[], dataRows: any[][]): number[] {
  const dateColumnIndices: number[] = [];
  
  // Date-related keywords in column headers
  const dateKeywords = [
    'date', 'time', 'created', 'updated', 'modified', 'start', 'end', 
    'week', 'month', 'year', 'day', 'timestamp', 'period', 'when'
  ];
  
  // Keywords that indicate NON-date columns (business metrics)
  const nonDateKeywords = [
    'sold', 'units', 'clicks', 'impressions', 'orders', 'sales', 'spend', 
    'cost', 'revenue', 'count', 'quantity', 'amount', 'price', 'rate', 
    'ratio', 'percentage', 'score', 'value', 'total', 'sum', 'avg', 'average'
  ];
  
  for (let colIndex = 0; colIndex < headers.length; colIndex++) {
    const header = headers[colIndex].toLowerCase();
    
    // FIRST: Check if header contains NON-date keywords (business metrics)
    // These should NEVER be treated as dates, even if they contain numbers
    const hasNonDateKeyword = nonDateKeywords.some(keyword => header.includes(keyword));
    if (hasNonDateKeyword) {
      console.log(`   📊 Skipping "${headers[colIndex]}" - contains business metric keyword`);
      continue; // Skip this column entirely
    }
    
    // SECOND: Check if header contains date-related keywords
    const hasDateKeyword = dateKeywords.some(keyword => header.includes(keyword));
    
    // If header has date keywords, it's likely a date column
    if (hasDateKeyword) {
      console.log(`   📅 "${headers[colIndex]}" detected as date column (header keyword match)`);
      dateColumnIndices.push(colIndex);
      continue;
    }
    
    // THIRD: For headers without clear keywords, analyze the data more carefully
    const columnValues = dataRows
      .slice(0, Math.min(10, dataRows.length)) // Check first 10 rows
      .map(row => row[colIndex])
      .filter(val => val !== null && val !== undefined && val !== "");
    
    if (columnValues.length === 0) continue;
    
    // Check if values look like Excel date serial numbers
    // Be more restrictive: only consider as dates if values are in a reasonable date range
    const looksLikeDateSerial = columnValues.every(val => {
      if (typeof val !== 'number') return false;
      
      // Excel date serial numbers for reasonable date ranges:
      // - 36526 = 2000-01-01
      // - 45000+ = 2023+
      // - Your example shows 45873, 45874 which are 2025 dates
      // 
      // Exclude small numbers that are likely business metrics:
      // - Numbers < 30000 are likely not dates (before ~1982)
      // - This excludes small values like 52, 70, 1204 (your business metrics)
      return val >= EXCEL_DATE.SERIAL_MODERN_MIN && val < EXCEL_DATE.SERIAL_MAX && Number.isInteger(val);
    });
    
    // Only treat as date if:
    // 1. Values look like modern Excel date serials (30000+), AND
    // 2. Header doesn't contain business metric keywords
    if (looksLikeDateSerial) {
      console.log(`   📅 "${headers[colIndex]}" detected as date column (data pattern match)`);
      dateColumnIndices.push(colIndex);
    }
  }
  
  return dateColumnIndices;
}

/**
 * Convert Excel date serial number to readable date string
 * Excel stores dates as the number of days since January 1, 1900
 * 
 * @param serialNumber - Excel date serial number
 * @returns Formatted date string (YYYY-MM-DD) or original value if not a valid date
 */
function formatExcelDate(serialNumber: any): any {
  // If it's null, undefined, or empty string, return as-is
  if (serialNumber === null || serialNumber === undefined || serialNumber === "") {
    return serialNumber;
  }
  
  // If it's not a number, return as-is
  if (typeof serialNumber !== 'number') {
    return serialNumber;
  }
  
  // Validate that it's a reasonable Excel date serial number
  // Excel dates typically range from 1 (1900-01-01) to ~50000+ (modern dates)
  // Your example shows 45873, 45874 which should be around 2025
  if (serialNumber < 1 || serialNumber > EXCEL_DATE.SERIAL_MAX || !Number.isInteger(serialNumber)) {
    console.warn(`⚠️ Invalid Excel date serial number: ${serialNumber}, returning as-is`);
    return serialNumber;
  }
  
  try {
    // Excel date serial number starts from 1900-01-01
    // But Excel incorrectly treats 1900 as a leap year, so we need to adjust
    const excelEpoch = new Date(1900, 0, 1); // January 1, 1900
    
    // Subtract 2 days to account for:
    // 1. Excel's 1-based indexing (day 1 = 1900-01-01)
    // 2. Excel's leap year bug (treats 1900 as leap year)
    const adjustedDays = serialNumber - 2;
    
    // Create the date
    const date = new Date(excelEpoch.getTime() + (adjustedDays * 24 * 60 * 60 * 1000));
    
    // Validate the resulting date
    if (isNaN(date.getTime())) {
      console.warn(`⚠️ Invalid date created from serial ${serialNumber}, returning original value`);
      return serialNumber;
    }
    
    // Format as YYYY-MM-DD for consistency
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    const formattedDate = `${year}-${month}-${day}`;
    return formattedDate;
  } catch (error) {
    console.error(`❌ Error converting Excel date serial ${serialNumber}:`, error);
    return serialNumber; // Return original value on error
  }
}

/**
 * Convert Excel column number to letter (1 = A, 27 = AA, etc.)
 */
function getExcelColumnLetter(columnNumber: number): string {
  let columnLetter = "";
  let temp = columnNumber;

  while (temp > 0) {
    const remainder = (temp - 1) % 26;
    columnLetter = String.fromCharCode(65 + remainder) + columnLetter;
    temp = Math.floor((temp - 1) / 26);
  }

  return columnLetter;
}

/**
 * Preview data from a table (first 5 rows) for display in UI
 */
export function getTablePreview(tableData: ExcelTableData, maxRows: number = 5): any[][] {
  if (!tableData || !tableData.data) {
    return [];
  }
  
  return tableData.data.slice(0, Math.min(maxRows, tableData.data.length));
}

/**
 * Get column statistics for a specific column
 */
export function getColumnStats(tableData: ExcelTableData, columnIndex: number): {
  columnName: string;
  totalValues: number;
  nullValues: number;
  uniqueValues: number;
  sampleValues: any[];
} {
  const columnName = tableData.headers[columnIndex] || `Column ${columnIndex + 1}`;
  const columnValues = tableData.data.map(row => row[columnIndex]);
  
  const nonNullValues = columnValues.filter(v => v !== null && v !== undefined && v !== "");
  const uniqueValues = new Set(nonNullValues);
  
  return {
    columnName,
    totalValues: columnValues.length,
    nullValues: columnValues.length - nonNullValues.length,
    uniqueValues: uniqueValues.size,
    sampleValues: Array.from(uniqueValues).slice(0, 5)
  };
}

